import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Typography, Button, CircularProgress, Alert, Tab, Tabs,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  Chip, Dialog, DialogTitle, DialogContent, DialogActions,
  Drawer, IconButton, Divider, Autocomplete, TextField, Snackbar, Paper,
} from "@mui/material";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { phappsRequest } from "@/services/apiService";
import { API_PATHS } from "@/config/apiConstants";
import ZephyrIconWhite from "@/Images/Icons/Zephyr_Icon_White.png";

const Z_PURPLE = "#38184f";
const Z_BLUE   = "#2baede";
const fmt = (v) => new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD" }).format(Number(v ?? 0));
const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-AU") : "—";

// ── ABA download (needs raw blob, not phappsRequest JSON) ────────────────────
async function downloadABA(batchID) {
  const token = localStorage.getItem("jwtToken");
  const baseUrl = import.meta.env.VITE_APP_DATA_API_URL;
  const url = `${baseUrl}${API_PATHS.ZEPHYR_STATEMENTS_ABA}/${batchID}`;
  const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) throw new Error("Failed to generate ABA file");
  const text = await res.text();
  const blob = new Blob([text], { type: "text/plain" });
  const objUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objUrl;
  a.download = `ZC_EFT_batch${batchID}.aba`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(objUrl);
}

// ── Generate Confirm Dialog ──────────────────────────────────────────────────
function GenerateDialog({ open, onClose, onConfirm, alreadyRun, generating }) {
  return (
    <Dialog open={open} onClose={() => !generating && onClose()} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}>
      <DialogTitle sx={{
        background: `linear-gradient(135deg, ${Z_PURPLE}, ${Z_BLUE})`, color: "#fff",
        display: "flex", alignItems: "center", justifyContent: "space-between", py: 1.5,
      }}>
        <span>Generate Monthly Statements</span>
        <IconButton onClick={onClose} size="small" sx={{ color: "#fff" }} disabled={generating}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: "20px !important" }}>
        {alreadyRun && (
          <Alert severity="error" icon={<WarningAmberIcon />} sx={{ mb: 2 }}>
            <strong>Statements have already been run today.</strong> Running again will create
            a second batch and re-reconcile any newly paid scripts. Only proceed if you are sure.
          </Alert>
        )}
        <Alert severity="warning" sx={{ mb: 1 }}>
          This will reconcile <strong>all unreconciled paid scripts</strong> across all eligible
          chemists (those with BSB and Account Number on file). This action cannot be undone.
        </Alert>
        <Typography variant="body2" color="text.secondary">
          Chemists missing bank details will be excluded and listed in the results summary.
          A notification email will be sent to the admin address on completion.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: "none" }} disabled={generating}>Cancel</Button>
        <Button
          variant="contained"
          color="error"
          onClick={onConfirm}
          disabled={generating}
          startIcon={generating ? <CircularProgress size={16} color="inherit" /> : <PlayArrowIcon />}
          sx={{ textTransform: "none", fontWeight: 700 }}
        >
          {generating ? "Generating…" : "Yes, Generate Statements"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Generate Result Card ─────────────────────────────────────────────────────
function GenerateResultCard({ result, onClose }) {
  const { batchID, processed, missingBankDetails } = result;
  const totalChemVal  = processed.reduce((s, c) => s + c.chemval, 0);
  const totalZephyrVal = processed.reduce((s, c) => s + c.zephyrsum, 0);
  const totalReconSum = processed.reduce((s, c) => s + c.reconsum, 0);

  return (
    <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden", borderColor: "#10b981", borderWidth: 2, mb: 2 }}>
      <Box sx={{ background: "#f0fdf4", p: 2, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <CheckCircleOutlineIcon sx={{ color: "#059669" }} />
          <Typography fontWeight={700} sx={{ color: "#059669" }}>
            Batch #{batchID} generated — {processed.length} chemist(s) reconciled
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
      </Box>
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: "flex", gap: 3, mb: 2, flexWrap: "wrap" }}>
          <Box><Typography variant="caption" color="text.secondary" fontWeight={700}>Total Scripts</Typography>
            <Typography fontWeight={700}>{fmt(totalReconSum)}</Typography></Box>
          <Box><Typography variant="caption" color="text.secondary" fontWeight={700}>Zephyr Fees (15%)</Typography>
            <Typography fontWeight={700} sx={{ color: Z_BLUE }}>{fmt(totalZephyrVal)}</Typography></Box>
          <Box><Typography variant="caption" color="text.secondary" fontWeight={700}>Owing to Chemists</Typography>
            <Typography fontWeight={700} sx={{ color: Z_PURPLE }}>{fmt(totalChemVal)}</Typography></Box>
        </Box>

        {processed.length > 0 && (
          <TableContainer sx={{ border: "1px solid #e2e8f0", borderRadius: 1, mb: 2 }}>
            <Table size="small">
              <TableHead sx={{ background: `${Z_PURPLE}08` }}>
                <TableRow>
                  {["Chemist", "Script Total", "Zephyr Fee", "Chemist Owing"].map(h => (
                    <TableCell key={h} sx={{ fontWeight: 700, fontSize: "0.78rem", color: Z_PURPLE, py: 0.75 }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {processed.map(c => (
                  <TableRow key={c.chemShortCode} sx={{ "& td": { py: 0.5, fontSize: "0.8rem" } }}>
                    <TableCell sx={{ fontWeight: 600 }}>{c.chemistName}</TableCell>
                    <TableCell align="right">{fmt(c.reconsum)}</TableCell>
                    <TableCell align="right">{fmt(c.zephyrsum)}</TableCell>
                    <TableCell align="right">{fmt(c.chemval)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {missingBankDetails.length > 0 && (
          <>
            <Alert severity="warning" sx={{ mb: 1, py: 0.5 }}>
              <strong>{missingBankDetails.length} chemist(s) excluded</strong> — missing BSB or Account Number.
              Their scripts remain unreconciled.
            </Alert>
            <TableContainer sx={{ border: "1px solid #fed7aa", borderRadius: 1 }}>
              <Table size="small">
                <TableHead sx={{ background: "#fff7ed" }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, fontSize: "0.78rem", color: "#92400e", py: 0.75 }}>Chemist</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: "0.78rem", color: "#92400e", py: 0.75 }}>Pending Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {missingBankDetails.map(m => (
                    <TableRow key={m.chemShortCode} sx={{ "& td": { py: 0.5, fontSize: "0.8rem" } }}>
                      <TableCell>{m.chemistName}</TableCell>
                      <TableCell align="right">{fmt(m.reconsum)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </Box>
    </Paper>
  );
}

// ── Batch Detail Drawer ──────────────────────────────────────────────────────
function BatchDetailDrawer({ batchID, onClose }) {
  const [detail, setDetail]   = useState(null);
  const [loading, setLoading] = useState(false);
  const [abaLoading, setAbaLoading] = useState(false);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    if (!batchID) return;
    setLoading(true);
    setDetail(null);
    phappsRequest(`${API_PATHS.ZEPHYR_STATEMENTS_HISTORY}/${batchID}`, "GET")
      .then(res => setDetail(res?.data ?? []))
      .finally(() => setLoading(false));
  }, [batchID]);

  const handleDownloadABA = async () => {
    setAbaLoading(true);
    try {
      await downloadABA(batchID);
    } catch (err) {
      setToast(err.message);
    } finally {
      setAbaLoading(false);
    }
  };

  const totalChemVal = (detail ?? []).reduce((s, r) => s + r.chemVal, 0);
  const totalZephyr  = (detail ?? []).reduce((s, r) => s + r.zephyrVal, 0);
  const totalRecon   = (detail ?? []).reduce((s, r) => s + r.reconsum, 0);

  return (
    <>
      <Drawer anchor="right" open={!!batchID} onClose={onClose}
        PaperProps={{ sx: { width: { xs: "100%", sm: 620 }, p: 0 } }}>
        <Box sx={{
          background: `linear-gradient(135deg, ${Z_PURPLE}, ${Z_BLUE})`, px: 2.5, py: 1.5,
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          <Typography fontWeight={700} sx={{ color: "#fff" }}>Batch #{batchID} — Detail</Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              size="small" variant="outlined"
              startIcon={abaLoading ? <CircularProgress size={14} color="inherit" /> : <DownloadOutlinedIcon />}
              onClick={handleDownloadABA}
              disabled={abaLoading || loading}
              sx={{ textTransform: "none", color: "#fff", borderColor: "rgba(255,255,255,0.5)",
                "&:hover": { borderColor: "#fff", background: "rgba(255,255,255,0.1)" } }}
            >
              {abaLoading ? "Building…" : "Download ABA"}
            </Button>
            <IconButton onClick={onClose} size="small" sx={{ color: "#fff" }}><CloseIcon /></IconButton>
          </Box>
        </Box>

        <Box sx={{ p: 2.5, overflow: "auto", flex: 1 }}>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", pt: 4 }}>
              <CircularProgress sx={{ color: Z_PURPLE }} />
            </Box>
          ) : detail ? (
            <>
              <Box sx={{ display: "flex", gap: 3, mb: 2, flexWrap: "wrap" }}>
                <Box><Typography variant="caption" color="text.secondary" fontWeight={700}>Script Total</Typography>
                  <Typography fontWeight={700}>{fmt(totalRecon)}</Typography></Box>
                <Box><Typography variant="caption" color="text.secondary" fontWeight={700}>Zephyr Fees</Typography>
                  <Typography fontWeight={700} sx={{ color: Z_BLUE }}>{fmt(totalZephyr)}</Typography></Box>
                <Box><Typography variant="caption" color="text.secondary" fontWeight={700}>Owing to Chemists</Typography>
                  <Typography fontWeight={700} sx={{ color: Z_PURPLE }}>{fmt(totalChemVal)}</Typography></Box>
              </Box>
              <TableContainer sx={{ border: "1px solid #e2e8f0", borderRadius: 1 }}>
                <Table size="small">
                  <TableHead sx={{ background: `${Z_PURPLE}08` }}>
                    <TableRow>
                      {["Chemist", "Script Total", "Zephyr Fee", "Chemist Owing", "BSB", "Account"].map(h => (
                        <TableCell key={h} sx={{ fontWeight: 700, fontSize: "0.78rem", color: Z_PURPLE, py: 0.75 }}>{h}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {detail.map(row => (
                      <TableRow key={row.reconID} sx={{ "& td": { py: 0.6, fontSize: "0.8rem" } }}>
                        <TableCell sx={{ fontWeight: 600 }}>{row.chemistName}</TableCell>
                        <TableCell align="right">{fmt(row.reconsum)}</TableCell>
                        <TableCell align="right">{fmt(row.zephyrVal)}</TableCell>
                        <TableCell align="right">{fmt(row.chemVal)}</TableCell>
                        <TableCell>{row.bsb || "—"}</TableCell>
                        <TableCell>{row.accountNumber || "—"}</TableCell>
                      </TableRow>
                    ))}
                    {detail.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6}>
                          <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>No data.</Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          ) : null}
        </Box>
      </Drawer>

      <Snackbar open={!!toast} autoHideDuration={4000} onClose={() => setToast(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert severity="error" onClose={() => setToast(null)}>{toast}</Alert>
      </Snackbar>
    </>
  );
}

// ── Statement Detail Drawer (scripts) ────────────────────────────────────────
function StatementDetailDrawer({ reconID, chemistName, onClose }) {
  const [scripts, setScripts] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!reconID) return;
    setLoading(true);
    setScripts(null);
    phappsRequest(`${API_PATHS.ZEPHYR_STATEMENTS_DETAIL}/${reconID}`, "GET")
      .then(res => setScripts(res?.data ?? []))
      .finally(() => setLoading(false));
  }, [reconID]);

  return (
    <Drawer anchor="right" open={!!reconID} onClose={onClose}
      PaperProps={{ sx: { width: { xs: "100%", sm: 680 }, p: 0 } }}>
      <Box sx={{
        background: `linear-gradient(135deg, ${Z_PURPLE}, ${Z_BLUE})`, px: 2.5, py: 1.5,
        display: "flex", alignItems: "center", justifyContent: "space-between",
      }}>
        <Box>
          <Typography fontWeight={700} sx={{ color: "#fff" }}>Statement Scripts</Typography>
          {chemistName && <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.8)" }}>{chemistName}</Typography>}
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: "#fff" }}><CloseIcon /></IconButton>
      </Box>

      <Box sx={{ p: 2.5, overflow: "auto", flex: 1 }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", pt: 4 }}>
            <CircularProgress sx={{ color: Z_PURPLE }} />
          </Box>
        ) : scripts ? (
          <TableContainer sx={{ border: "1px solid #e2e8f0", borderRadius: 1 }}>
            <Table size="small">
              <TableHead sx={{ background: `${Z_PURPLE}08` }}>
                <TableRow>
                  {["Patient", "Claim #", "Date", "Item", "Qty", "Invoiced", "Received"].map(h => (
                    <TableCell key={h} sx={{ fontWeight: 700, fontSize: "0.78rem", color: Z_PURPLE, py: 0.75 }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {scripts.map(s => (
                  <TableRow key={s.ScriptID} sx={{ "& td": { py: 0.5, fontSize: "0.8rem" } }}>
                    <TableCell>{s.FullName ?? "—"}</TableCell>
                    <TableCell>{s.ClaimNumber ?? "—"}</TableCell>
                    <TableCell>{fmtDate(s.DateDispensed)}</TableCell>
                    <TableCell>{s.txtItem}</TableCell>
                    <TableCell align="center">{s.Qty ?? "—"}</TableCell>
                    <TableCell align="right">{s.InvAmount != null ? fmt(s.InvAmount) : "—"}</TableCell>
                    <TableCell align="right">{s.RcvdAmount != null ? fmt(s.RcvdAmount) : "—"}</TableCell>
                  </TableRow>
                ))}
                {scripts.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7}>
                      <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>No scripts.</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        ) : null}
      </Box>
    </Drawer>
  );
}

// ── History Tab ──────────────────────────────────────────────────────────────
function HistoryTab() {
  const [history, setHistory]         = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [selectedBatch, setSelectedBatch]   = useState(null);
  const [dialogOpen, setDialogOpen]         = useState(false);
  const [alreadyRun, setAlreadyRun]         = useState(false);
  const [generating, setGenerating]         = useState(false);
  const [generateResult, setGenerateResult] = useState(null);
  const [abaLoadingID, setAbaLoadingID]     = useState(null);
  const [toast, setToast]                   = useState(null);

  const loadHistory = useCallback(() => {
    setHistoryLoading(true);
    phappsRequest(API_PATHS.ZEPHYR_STATEMENTS_HISTORY, "GET")
      .then(res => setHistory(res?.data ?? []))
      .catch(() => setHistory([]))
      .finally(() => setHistoryLoading(false));
  }, []);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  const handleGenerateClick = async () => {
    try {
      const res = await phappsRequest(API_PATHS.ZEPHYR_STATEMENTS_CHECK_TODAY, "GET");
      setAlreadyRun(res?.data?.alreadyRun ?? false);
    } catch (_) {
      setAlreadyRun(false);
    }
    setDialogOpen(true);
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const res = await phappsRequest(API_PATHS.ZEPHYR_STATEMENTS_GENERATE, "POST");
      setGenerateResult(res?.data);
      setDialogOpen(false);
      loadHistory();
    } catch (err) {
      setToast(err.message || "Generation failed");
      setDialogOpen(false);
    } finally {
      setGenerating(false);
    }
  };

  const handleDownloadABA = async (batchID, e) => {
    e.stopPropagation();
    setAbaLoadingID(batchID);
    try {
      await downloadABA(batchID);
    } catch (err) {
      setToast(err.message);
    } finally {
      setAbaLoadingID(null);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}>
        <Typography variant="body2" color="text.secondary">
          All past statement runs. Click a row to view detail.
        </Typography>
        <Button
          variant="contained"
          startIcon={<PlayArrowIcon />}
          onClick={handleGenerateClick}
          sx={{
            textTransform: "none", fontWeight: 700,
            background: `linear-gradient(135deg, ${Z_PURPLE}, ${Z_BLUE})`,
          }}
        >
          Generate Monthly Statements
        </Button>
      </Box>

      {generateResult && (
        <GenerateResultCard result={generateResult} onClose={() => setGenerateResult(null)} />
      )}

      {historyLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", pt: 4 }}>
          <CircularProgress sx={{ color: Z_PURPLE }} />
        </Box>
      ) : (
        <TableContainer sx={{ border: "1px solid #e2e8f0", borderRadius: 1.5, background: "#fff" }}>
          <Table size="small">
            <TableHead sx={{ background: `${Z_PURPLE}08` }}>
              <TableRow>
                {["Batch #", "Date", "Chemists", "Script Total", "Zephyr Fees", "Owing to Chemists", ""].map(h => (
                  <TableCell key={h} sx={{ fontWeight: 700, fontSize: "0.78rem", color: Z_PURPLE, py: 1 }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {(history ?? []).map(row => (
                <TableRow
                  key={row.batchID}
                  hover
                  onClick={() => setSelectedBatch(row.batchID)}
                  sx={{ cursor: "pointer", "& td": { py: 0.75, fontSize: "0.82rem" } }}
                >
                  <TableCell sx={{ fontWeight: 600, color: Z_PURPLE }}>#{row.batchID}</TableCell>
                  <TableCell>{fmtDate(row.reconDate)}</TableCell>
                  <TableCell align="center">{row.chemistCount}</TableCell>
                  <TableCell align="right">{fmt(row.totalReconSum)}</TableCell>
                  <TableCell align="right" sx={{ color: Z_BLUE }}>{fmt(row.totalZephyrVal)}</TableCell>
                  <TableCell align="right" sx={{ color: Z_PURPLE, fontWeight: 600 }}>{fmt(row.totalChemVal)}</TableCell>
                  <TableCell sx={{ width: "1px", whiteSpace: "nowrap" }}>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={abaLoadingID === row.batchID
                        ? <CircularProgress size={12} />
                        : <DownloadOutlinedIcon fontSize="small" />}
                      onClick={(e) => handleDownloadABA(row.batchID, e)}
                      disabled={abaLoadingID === row.batchID}
                      sx={{ textTransform: "none", fontSize: "0.72rem", py: 0.25,
                        borderColor: Z_PURPLE, color: Z_PURPLE }}
                    >
                      ABA
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {history?.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7}>
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>No statement runs yet.</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <GenerateDialog
        open={dialogOpen}
        onClose={() => !generating && setDialogOpen(false)}
        onConfirm={handleGenerate}
        alreadyRun={alreadyRun}
        generating={generating}
      />

      <BatchDetailDrawer
        batchID={selectedBatch}
        onClose={() => setSelectedBatch(null)}
      />

      <Snackbar open={!!toast} autoHideDuration={4000} onClose={() => setToast(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert severity="error" onClose={() => setToast(null)}>{toast}</Alert>
      </Snackbar>
    </Box>
  );
}

// ── Chemist Tab ──────────────────────────────────────────────────────────────
function ChemistTab() {
  const [chemists, setChemists]             = useState([]);
  const [chemistsLoading, setChemistsLoading] = useState(false);
  const [selectedChemist, setSelectedChemist] = useState(null);
  const [statements, setStatements]         = useState(null);
  const [statementsLoading, setStatementsLoading] = useState(false);
  const [selectedStatement, setSelectedStatement] = useState(null);

  useEffect(() => {
    setChemistsLoading(true);
    phappsRequest(API_PATHS.ZEPHYR_ADMIN_ZEPHYR_CHEMISTS, "GET")
      .then(res => setChemists(res?.data ?? []))
      .catch(() => setChemists([]))
      .finally(() => setChemistsLoading(false));
  }, []);

  useEffect(() => {
    if (!selectedChemist) { setStatements(null); return; }
    setStatementsLoading(true);
    setStatements(null);
    phappsRequest(`${API_PATHS.ZEPHYR_STATEMENTS_CHEMIST}/${selectedChemist.ChemShortCode}`, "GET")
      .then(res => setStatements(res?.data ?? []))
      .catch(() => setStatements([]))
      .finally(() => setStatementsLoading(false));
  }, [selectedChemist]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Autocomplete
        options={chemists}
        loading={chemistsLoading}
        getOptionLabel={c => `${c.ChemistName ?? ""} (${c.ChemShortCode ?? ""})`}
        value={selectedChemist}
        onChange={(_, v) => setSelectedChemist(v)}
        isOptionEqualToValue={(a, b) => a.ChemShortCode === b.ChemShortCode}
        sx={{ maxWidth: 400 }}
        renderInput={(params) => (
          <TextField {...params} label="Select Chemist" size="small"
            InputProps={{ ...params.InputProps,
              endAdornment: <>{chemistsLoading && <CircularProgress size={16} />}{params.InputProps.endAdornment}</> }} />
        )}
      />

      {statementsLoading && (
        <Box sx={{ display: "flex", justifyContent: "center", pt: 3 }}>
          <CircularProgress sx={{ color: Z_PURPLE }} />
        </Box>
      )}

      {statements !== null && !statementsLoading && (
        <TableContainer sx={{ border: "1px solid #e2e8f0", borderRadius: 1.5, background: "#fff" }}>
          <Table size="small">
            <TableHead sx={{ background: `${Z_PURPLE}08` }}>
              <TableRow>
                {["Batch #", "Date", "Script Total", "Zephyr Fee", "Chemist Value"].map(h => (
                  <TableCell key={h} sx={{ fontWeight: 700, fontSize: "0.78rem", color: Z_PURPLE, py: 1 }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {statements.map(row => (
                <TableRow
                  key={row.reconID}
                  hover
                  onClick={() => setSelectedStatement(row)}
                  sx={{ cursor: "pointer", "& td": { py: 0.75, fontSize: "0.82rem" } }}
                >
                  <TableCell sx={{ fontWeight: 600, color: Z_PURPLE }}>#{row.batchID}</TableCell>
                  <TableCell>{fmtDate(row.reconDate)}</TableCell>
                  <TableCell align="right">{fmt(row.reconsum)}</TableCell>
                  <TableCell align="right" sx={{ color: Z_BLUE }}>{fmt(row.zephyrVal)}</TableCell>
                  <TableCell align="right" sx={{ color: Z_PURPLE, fontWeight: 600 }}>{fmt(row.chemVal)}</TableCell>
                </TableRow>
              ))}
              {statements.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5}>
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                      No statements found for this chemist.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <StatementDetailDrawer
        reconID={selectedStatement?.reconID ?? null}
        chemistName={selectedChemist?.ChemistName}
        onClose={() => setSelectedStatement(null)}
      />
    </Box>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function ZephyrStatementsPage() {
  const [tab, setTab] = useState(0);

  return (
    <Box sx={{ p: 3, display: "flex", flexDirection: "column", gap: 2, height: "100%", minHeight: 0, overflow: "auto" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexShrink: 0 }}>
        <Box sx={{
          width: 36, height: 36, borderRadius: 1.5,
          background: `linear-gradient(135deg, ${Z_PURPLE}, ${Z_BLUE})`,
          display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
        }}>
          <img src={ZephyrIconWhite} alt="" style={{ height: 20, objectFit: "contain" }} />
        </Box>
        <Typography fontWeight={800} fontSize="1.25rem" sx={{ color: Z_PURPLE }}>Statements</Typography>
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: "divider", flexShrink: 0 }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{
            "& .MuiTab-root": { textTransform: "none", fontWeight: 600, minWidth: 100 },
            "& .Mui-selected": { color: Z_PURPLE },
            "& .MuiTabs-indicator": { backgroundColor: Z_PURPLE },
          }}
        >
          <Tab label="History" />
          <Tab label="Chemist" />
        </Tabs>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0 }}>
        {tab === 0 && <HistoryTab />}
        {tab === 1 && <ChemistTab />}
      </Box>
    </Box>
  );
}
